import { parseString } from 'xml2js'
import axios from 'axios'

function convertAMPMto24Hs(time12h) {
  const [time, period] = time12h.split(/(?=[AP]M)/) // Splitting time and period (AM/PM)
  let [hours, minutes] = time.split(':')

  hours = parseInt(hours)
  minutes = parseInt(minutes)

  if (period.toUpperCase() === 'PM' && hours < 12) {
    hours += 12
  } else if (period.toUpperCase() === 'AM' && hours === 12) {
    hours = 0
  }

  // Padding hours and minutes with leading zeros if necessary
  hours = hours < 10 ? '0' + hours : hours.toString()
  minutes = minutes < 10 ? '0' + minutes : minutes.toString()

  return hours + ':' + minutes
}

function convertDateFormat(inputDate) {
  // Split the input string by '-'
  var parts = inputDate.split('-')

  // Reorder the parts as per the desired format
  var outputDate = parts[2] + '/' + parts[1] + '/' + parts[0]

  return outputDate
}

function createNewReservationXML(
  token,
  lcode,
  dfrom,
  dto,
  rooms,
  customer,
  amount,
  guests,
) {

  let xmlString =
    '<methodCall>' +
    '<methodName>new_reservation</methodName>' +
    '<params>' +
    '<param>' +
    '<value>' +
    '<string>' +
    token +
    '</string>' +
    '</value>' +
    '</param>' +
    '<param>' +
    '<value>' +
    '<int>' +
    lcode +
    '</int>' +
    '</value>' +
    '</param>' +
    '<param>' +
    '<value>' +
    '<string>' +
    dfrom +
    '</string>' +
    '</value>' +
    '</param>' +
    '<param>' +
    '<value>' +
    '<string>' +
    dto +
    '</string>' +
    '</value>' +
    '</param>' +
    '<param>' +
    '<value>' +
    '<struct>' +
    Object.entries(rooms)
      .map(
        ([room, [quantity, board]]) =>
          '<member>' +
          '<name>' +
          room +
          '</name>' +
          '<value>' +
          '<array>' +
          '<data>' +
          '<value>' +
          '<int>' +
          quantity +
          '</int>' +
          '</value>' +
          '<value>' +
          '<string>' +
          board +
          '</string>' +
          '</value>' +
          '</data>' +
          '</array>' +
          '</value>' +
          '</member>',
      )
      .join('') +
    '</struct>' +
    '</value>' +
    '</param>' +
    '<param>' +
    '<value>' +
    '<struct>' +
    Object.entries(customer)
      .map(
        ([key, value]) =>
          '<member>' +
          '<name>' +
          key +
          '</name>' +
          '<value>' +
          '<string>' +
          value +
          '</string>' +
          '</value>' +
          '</member>',
      )
      .join('') +
    '</struct>' +
    '</value>' +
    '</param>' +
    '<param>' +
    '<value>' +
    '<string>' +
    amount +
    '</string>' +
    '</value>' +
    '</param>' +
    '<param>' +
    '<value>' +
    '<struct>' +
    '<member>' +
    '<name>men</name>' +
    '<value>' +
    '<int>' +
    guests.men +
    '</int>' +
    '</value>' +
    '</member>' +
    '<member>' +
    '<name>children</name>' +
    '<value>' +
    '<int>' +
    guests.children +
    '</int>' +
    '</value>' +
    '</member>' +
    '</struct>' +
    '</value>' +
    '</param>' +
    '</params>' +
    '</methodCall>'

  return xmlString
}

function parseXmlToJson(xml) {
  return new Promise((resolve, reject) => {
    parseString(xml, { explicitArray: false, trim: true }, (error, result) => {
      if (error) {
        reject(error)
      } else {
        resolve(result)
      }
    })
  })
}

function simplifyReservation(response) {
  const values = response.methodResponse?.params?.param?.value?.array?.data?.value
  const reservation = {
    int: values[0].int,
    string: values[1].string,
  }
  return reservation
}

function simplifyResponse(response) {
  const data = response.methodResponse.params.param.value.array.data.value;
  return {
    int: parseInt(data[0].int),
    string: data[1].string,
  };
}

function simplifyRestrictionPlans(response) {
  // Use traditional checks to access deeply nested properties
  const props = response.methodResponse?.params?.param?.value?.array?.data?.value[1]?.array?.data?.value?.struct?.member;

  let simplifiedReservation = {}; // Initialize a new object for each reservation

  if (props && Array.isArray(props)) {
    props.forEach(property => {
      // Switch to match different property names
      switch (property.name) {
        case 'id':
        case 'name':
          // Assign the correct value type to the corresponding property
          simplifiedReservation[property.name] = property.value && (property.value.string || property.value.int || property.value.double || property.value.array);
          break;
        // You can add more cases here if you expect more properties
        default:
          console.log("Property not matched:", property.name);
      }
    });
  } else {
    console.log("Properties not found or not an array");
  }

  return [simplifiedReservation]; // Return an array containing the simplified reservation object
}

const blockRoomsForDateRange = async (token, lcode, pid, startDate, endDate, formattedStartDate, roomIds, closeInt) => {
  const roomsValues = getRoomsValuesForDateRange(startDate, endDate, roomIds, closeInt);

  const body = `
    <methodCall>
      <methodName>rplan_update_rplan_values</methodName>
      <params>
        <param>
          <value>
            <string>${token}</string>
          </value>
        </param>
        <param>
          <value>
            <string>${lcode}</string>
          </value>
        </param>
        <param>
          <value>
            <int>${pid}</int>
          </value>
        </param>
        <param>
          <value>
            <string>${formattedStartDate}</string>
          </value>
        </param>
        <param>
          <value>
            <struct>
              ${roomsValues}
            </struct>
          </value>
        </param>
      </params>
    </methodCall>`;

  try {
    return await axios
      .post(
        `${process.env.REACT_APP_CHANNEL_API}`,
        body,
        {
          headers: {
            'Content-Type': 'text/plain',
          },
          responseType: 'text',
        },
      ).then(async (response) => {
        const parsedXml = await parseXmlToJson(response.data)
        return simplifyResponse(parsedXml)
      })

  } catch (error) {
    console.error('Error in blockRoomsForDateRange:', error);

    // Return an error message or null to ensure the calling function knows it failed
    return null;
  }
};

const getRoomsValuesForDateRange = (startDate, endDate, roomIds, closeInt) => {
  const rooms = [];
  const stopDate = new Date(endDate);

  const addDateValue = () => `
      <value>
        <struct>
          <member>
            <name>closed</name>
            <value>
              <int>${closeInt}</int>
            </value>
          </member>
        </struct>
      </value>
    `;

  roomIds.forEach(roomId => {
    const dates = [];
    let date = new Date(startDate);
    while (date <= stopDate) {
      dates.push(addDateValue());
      date.setDate(date.getDate() + 1);
    }

    rooms.push(`
        <member>
          <name>${roomId}</name>
          <value>
            <array>
              <data>
                ${dates.join('')}
              </data>
            </array>
          </value>
        </member>
      `);
  });

  return rooms.join('');
}

const sendCloseAvailToChannel = async (roomIds, closeInt, init, wuBookApiKey, lcode) => {
  /*let rPlansIds = [];

  const xmlString = `
    <methodCall>
      <methodName>rplan_rplans</methodName>
      <params>
        <param>
          <value>
            <string>${wuBookApiKey}</string>
          </value>
        </param>
        <param>
          <value>
            <string>${lcode}</string>
          </value>
        </param>
      </params>
    </methodCall>`;

  try {
    await axios
      .post(
        `${process.env.REACT_APP_CHANNEL_API}`,
        xmlString,
        {
          headers: {
            'Content-Type': 'text/plain',
          },
          responseType: 'text',
        },
      )
      .then(async (response) => {
        const parsedXml = await parseXmlToJson(response.data);
        const simplifiedJSON = simplifyRestrictionPlans(parsedXml);

        if (Array.isArray(simplifiedJSON)) {
          simplifiedJSON.forEach((rPlans) => {
            if (rPlans && rPlans.id) {
              rPlansIds.push(rPlans.id);
            }
          });
        } else {
          console.error('Error: simplifiedJSON is not an array or is undefined.');
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        return [];  // Return an empty array on error
      });
  } catch (error) {
    console.error('Error in axios request:', error);
    return [];  // Return an empty array on error
  }*/

  function formatDate(date) {
    if (typeof date === 'string') return date;
    const month = date.month > 9 ? date.month : '0' + date.month;
    const day = date.day > 9 ? date.day : '0' + date.day;
    const year = date.year;
    return `${year}-${month}-${day}`;
  }

  function formatSlashDate(date) {
    if (typeof date === 'string') {
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    }

    return (
      date.day +
      '/' +
      (date.month < 10 ? '0' + date.month : date.month) +
      '/' +
      date.year
    );
  }

  // Wubook bloquea noches en vez de dias por lo que cuando nos referimos a una fecha hablamos de la noche de la misma
  const dateInit = formatDate(init);
  const dateEnd = formatDate(init);
  const formattedStartDate = formatSlashDate(init);

  /*if (rPlansIds.length === 0) {
    console.error('Error: rPlansIds array is empty, nothing to process.');
    return [];  // Return an empty array if no plans
  }*/

  try {
    const result = await blockRoomsForDateRange(
      wuBookApiKey,
      lcode,
      "0",
      dateInit,
      dateEnd,
      formattedStartDate,
      roomIds,
      closeInt
    );
    /*
    const promises = rPlansIds.map((plan) => {
      return blockRoomsForDateRange(
        wuBookApiKey,
        lcode,
        "0",
        dateInit,
        dateEnd,
        formattedStartDate,
        roomIds,
        closeInt
      );
    });

    const results = await Promise.all(promises);
    return results;*/

    return result;
  } catch (error) {
    console.error('Error processing blockRoomsForDateRange:', error);
    return [];  // Return an empty array on error
  }
};

export {
  convertAMPMto24Hs,
  convertDateFormat,
  createNewReservationXML,
  parseXmlToJson,
  simplifyReservation,
  simplifyRestrictionPlans,
  blockRoomsForDateRange,
  simplifyResponse,
  sendCloseAvailToChannel
}
